import { STRINGS } from '@/constants/strings';
import { playerSessionKeys } from '@/features/player-session/queries/query-keys';
import { getCurrentEnvUrls } from '@/libs/config';
import {
  getSession,
  sessionDocRef,
  updateDocMerge,
} from '@/libs/firebase/firestore/firestore-helpers';
import type { QueryClient } from '@tanstack/react-query';

function capitalizeWords(input: string): string {
  return input.replace(/\b\w+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function formatCheckpointTitle(checkpoint: string, badge: string): string {
  // check if forms
  if (checkpoint.includes('form')) {
    // fix full swing (kept as-is from Swift logic)
    const badgeUpdated = badge.replace(/full/g, 'full');
    // check if an atg sub category
    if (
      checkpoint.includes('chipping') ||
      checkpoint.includes('pitching') ||
      checkpoint.includes('sand') ||
      checkpoint.includes('ironshot') ||
      checkpoint.includes('drivershot')
    ) {
      const updatedName = checkpoint.replace(/\./g, ' ');
      return capitalizeWords(updatedName);
    } else {
      const updatedName = `${capitalizeWords(badgeUpdated)} Forms`;
      return updatedName;
    }
  }
  if (checkpoint.includes('compass') || checkpoint.includes('combine')) {
    if (checkpoint.includes('compass')) {
      const updatedName = `${capitalizeWords(badge)} ${checkpoint.replace(/compass/g, 'Compass ')}`;
      return updatedName;
    } else {
      const updatedName = `${capitalizeWords(badge)} ${checkpoint.replace(/combine/g, 'Combine ')}`;
      return updatedName;
    }
  }
  // adds spaces after known words
  let updatedName = checkpoint.replace(/short/g, 'short ');
  updatedName = updatedName.replace(/medium/g, 'medium ');
  updatedName = updatedName.replace(/long/g, 'long ');
  updatedName = updatedName.replace(/oppositesideofhole/g, 'opposite side of hole ');
  updatedName = updatedName.replace(/rulesandetiquette/g, 'rules and etiquette');
  updatedName = updatedName.replace(/mentalapproach/g, 'mental'); // helps handle occurrences when 'approach' is included and when it's not
  updatedName = updatedName.replace(/mental/g, 'mental approach');
  updatedName = updatedName.replace(/sandshot/g, 'sand shot ');
  // updatedName = updatedName.replace(/full/g, "full ");
  updatedName = updatedName.replace(/score/g, 'score ');
  updatedName = updatedName.replace(/skill1/g, 'skill 1');
  updatedName = updatedName.replace(/skill2/g, 'skill 2');
  // updatedName = updatedName.replace(/iron/g, "iron ");
  // updatedName = updatedName.replace(/driver/g, "driver ");
  updatedName = updatedName.replace(/chipping\./g, 'chipping ');
  updatedName = updatedName.replace(/putting\./g, 'putting ');
  updatedName = updatedName.replace(/pitching\./g, 'pitching ');
  updatedName = updatedName.replace(/sand\./g, 'sand ');
  updatedName = updatedName.replace(/drivershot\./g, 'drivershot ');
  updatedName = updatedName.replace(/ironshot\./g, 'ironshot ');
  return capitalizeWords(updatedName);
}

function formatAttemptLabel(raw: string): string {
  // iOS rule: "Attempted" -> "Incomplete Attempt"
  return raw.toLowerCase() === 'attempted' ? 'Incomplete Attempt' : capitalizeWords(raw);
}

export function buildPinUrl(pinPicture?: string): string | null {
  if (!pinPicture) return null;
  return `${getCurrentEnvUrls().pinUrl}${pinPicture?.replace?.('.png', '.svg')}?alt=media`;
}

export function getPinsDisplay(
  pins: playerSession.Pin[],
  options?: {
    filterLevel?: number;
    excludeLevel?: number;
  },
) {
  return pins
    .filter(pin => (options?.filterLevel ? pin.levelNumber === options.filterLevel : true))
    .filter(pin => (options?.excludeLevel ? pin.levelNumber !== options.excludeLevel : true))
    .map(pin => ({
      ...pin,
      imageUrl: buildPinUrl(pin.pinPicture),
    }))
    .sort((a, b) => (a.levelNumber ?? 0) - (b.levelNumber ?? 0));
}

export function buildDetailsByLevel(player: playerSession.SessionLookupData) {
  const attempts = player.attempts ?? [];
  const pins = player.pins ?? [];
  const sections: Array<{
    level: number;
    rows: Array<{
      title: string;
      statuses: string[];
    }>;
    pins: ReturnType<typeof getPinsDisplay>;
  }> = [];
  for (let level = 1; level <= 10; level++) {
    const levelAttempts = attempts.filter(a => a.level === level);
    const levelPins = getPinsDisplay(pins, { filterLevel: level });
    if (levelAttempts.length === 0 && levelPins.length === 0) continue;
    const rows = levelAttempts.map(a => ({
      title: formatCheckpointTitle(a.checkpoint_name, a.badge_name),
      statuses: [...a.attempts].reverse().map(formatAttemptLabel),
    }));
    sections.push({ level, rows, pins: levelPins });
  }
  return {
    hasAttempts: attempts.length > 0 || pins.length > 0,
    emptyMessage: STRINGS.playerSession.sessionSummary.emptyMessage,
    sections,
  };
}

type SessionPlayerProgressDoc = {
  roster?: string[] | null;
  attempts?: Record<string, playerSession.SessionAttempt[] | null | undefined> | null;
  pins?: Record<string, playerSession.Pin[] | null | undefined> | null;
};

/** Keep only session attempts for levels strictly below the player's new current level. */
export function filterSessionAttemptsBelowLevel(
  attempts: playerSession.SessionAttempt[],
  keepBelowLevel: number,
): playerSession.SessionAttempt[] {
  return attempts.filter(a => {
    const level = Number(a?.level);
    if (!Number.isFinite(level)) return true;
    return level < keepBelowLevel;
  });
}

/** Keep only session pins for levels strictly below the player's new current level. */
export function filterSessionPinsBelowLevel(
  pins: playerSession.Pin[],
  keepBelowLevel: number,
): playerSession.Pin[] {
  return pins.filter(pin => {
    const level = Number(pin?.levelNumber);
    if (!Number.isFinite(level)) return true;
    return level < keepBelowLevel;
  });
}

export function isPlayerInSessionRoster(
  roster: string[] | null | undefined,
  playerId: string,
): boolean {
  const pid = playerId.trim();
  if (!pid) return false;
  return (roster ?? []).some(id => String(id).trim() === pid);
}

/**
 * When profile level is lowered, drop this player's session attempts and pins for
 * levels at or above `fromLevel`.
 */
export async function clearPlayerSessionDataAfterLevelDowngrade(params: {
  sessionId: string;
  playerId: string;
  fromLevel: number;
}): Promise<boolean> {
  const sessionId = params.sessionId.trim();
  const playerId = params.playerId.trim();
  const fromLevel = params.fromLevel;
  if (!sessionId || !playerId || !Number.isFinite(fromLevel)) return false;

  const session = await getSession<SessionPlayerProgressDoc>(sessionId);
  if (!session || !isPlayerInSessionRoster(session.roster, playerId)) return false;

  const currentAttempts = session.attempts?.[playerId];
  const currentPins = session.pins?.[playerId];
  const hasAttempts = Array.isArray(currentAttempts) && currentAttempts.length > 0;
  const hasPins = Array.isArray(currentPins) && currentPins.length > 0;
  if (!hasAttempts && !hasPins) return false;

  const payload: SessionPlayerProgressDoc = {};
  if (hasAttempts) {
    const nextAttempts = filterSessionAttemptsBelowLevel(currentAttempts, fromLevel);

    if (nextAttempts.length !== currentAttempts.length) {
      payload.attempts = {
        [playerId]: nextAttempts,
      };
    }
  }
  if (hasPins) {
    const nextPins = filterSessionPinsBelowLevel(currentPins, fromLevel);
    if (nextPins.length !== currentPins.length) {
      payload.pins = {
        [playerId]: nextPins,
      };
    }
  }
  if (Object.keys(payload).length === 0) return false;

  await updateDocMerge(sessionDocRef(sessionId), payload);
  return true;
}

function patchPlayerInSessionRows(
  rows: playerSession.SessionLookupData[],
  playerId: string,
  nextLevel: number,
  didDowngradeLevel: boolean,
): playerSession.SessionLookupData[] {
  const pid = playerId.trim();
  if (!pid) return rows;

  let changed = false;
  const next = rows.map(row => {
    if (String(row.id ?? '').trim() !== pid) return row;
    changed = true;
    const updated: playerSession.SessionLookupData = { ...row, level: nextLevel };
    if (didDowngradeLevel) {
      updated.attempts = filterSessionAttemptsBelowLevel(row.attempts ?? [], nextLevel);
      updated.pins = filterSessionPinsBelowLevel(row.pins ?? [], nextLevel);
    }
    return updated;
  });

  return changed ? next : rows;
}

function patchPlayerProgressInSessionRows(
  rows: playerSession.SessionLookupData[],
  playerId: string,
  nextProgress: playerSession.Progress[],
): playerSession.SessionLookupData[] {
  const pid = playerId.trim();
  if (!pid) return rows;

  let changed = false;
  const next = rows.map(row => {
    if (String(row.id ?? '').trim() !== pid) return row;
    changed = true;
    return { ...row, progress: nextProgress };
  });

  return changed ? next : rows;
}

/** Optimistically sync session list/summary cache after a profile progress edit. */
export function patchSessionListPlayerAfterProgressUpdate(
  queryClient: QueryClient,
  params: { playerId: string; nextProgress: playerSession.Progress[] },
): void {
  const { playerId, nextProgress } = params;

  queryClient.setQueriesData({ queryKey: playerSessionKeys.all }, (old: unknown) => {
    if (!old || typeof old !== 'object') return old;

    if (
      'sessions' in old &&
      Array.isArray((old as playerSession.SortFilterSearchSessionsResult).sessions)
    ) {
      const prev = old as playerSession.SortFilterSearchSessionsResult;
      const sessions = patchPlayerProgressInSessionRows(prev.sessions, playerId, nextProgress);
      if (sessions === prev.sessions) return old;
      return { ...prev, sessions };
    }

    if ('data' in old && Array.isArray((old as { data: playerSession.SessionLookupData[] }).data)) {
      const prev = old as { data: playerSession.SessionLookupData[]; meta?: unknown };
      const data = patchPlayerProgressInSessionRows(prev.data, playerId, nextProgress);
      if (data === prev.data) return old;
      return { ...prev, data };
    }

    return old;
  });
}

/** Optimistically sync session list/summary cache after a profile level edit. */
export function patchSessionListPlayerAfterLevelUpdate(
  queryClient: QueryClient,
  params: { playerId: string; nextLevel: number; didDowngradeLevel: boolean },
): void {
  const { playerId, nextLevel, didDowngradeLevel } = params;

  queryClient.setQueriesData({ queryKey: playerSessionKeys.all }, (old: unknown) => {
    if (!old || typeof old !== 'object') return old;

    if (
      'sessions' in old &&
      Array.isArray((old as playerSession.SortFilterSearchSessionsResult).sessions)
    ) {
      const prev = old as playerSession.SortFilterSearchSessionsResult;
      const sessions = patchPlayerInSessionRows(
        prev.sessions,
        playerId,
        nextLevel,
        didDowngradeLevel,
      );
      if (sessions === prev.sessions) return old;
      return { ...prev, sessions };
    }

    if ('data' in old && Array.isArray((old as { data: playerSession.SessionLookupData[] }).data)) {
      const prev = old as { data: playerSession.SessionLookupData[]; meta?: unknown };
      const data = patchPlayerInSessionRows(prev.data, playerId, nextLevel, didDowngradeLevel);
      if (data === prev.data) return old;
      return { ...prev, data };
    }

    return old;
  });
}
